import type { EntityFormErrorMap } from '@klear/ui/entity-form-state'
import type { Draft } from 'immer'
import { CREDIT_CLASSIFICATION_IG, CREDIT_CLASSIFICATION_INSURED, type CreditClassification } from '@klear/constants'
import { isValidLei, LEI_VALIDATION_MESSAGE } from '@klear/constants/lei'
import { moneyFromFormValue } from '@klear/ui/components/money-form-value'
import type { CustomerWizardFormValues } from '@klear/customer-ui/customer-commit-form'
import type { CustomerFormFieldPath } from '@klear/customer-ui/customer-form-state'

export const CUSTOMER_WIZARD_STEP = {
  TRADER: 'customer-trader',
  LOOKUP: 'customer-lookup',
  ORGANIZATION: 'customer-organization',
  LOCATION: 'customer-location',
  IDENTIFIERS: 'customer-identifiers',
  CREDIT: 'customer-credit',
  REVIEW: 'customer-review'
} as const

export type CustomerWizardStepId = (typeof CUSTOMER_WIZARD_STEP)[keyof typeof CUSTOMER_WIZARD_STEP]

export const CUSTOMER_CREDIT_FIELD_PATHS = [
  'creditClassification',
  'moodysRating',
  'spRating',
  'fitchRating',
  'jcrRating',
  'allianzRating',
  'allianzCreditLimit'
] as const satisfies readonly CustomerFormFieldPath[]

export function customerWizardStepFields(stepId: CustomerWizardStepId): CustomerFormFieldPath[] {
  switch (stepId) {
    case CUSTOMER_WIZARD_STEP.TRADER:
      return ['traderId']
    case CUSTOMER_WIZARD_STEP.ORGANIZATION:
      return ['name', 'description', 'parentOrganization', 'size']
    case CUSTOMER_WIZARD_STEP.LOCATION:
      return ['address.country', 'address.line1', 'address.city', 'address.state', 'address.postalCode', 'website']
    case CUSTOMER_WIZARD_STEP.IDENTIFIERS:
      return ['dunsNumber', 'lei', 'ein', 'vatNumber']
    case CUSTOMER_WIZARD_STEP.CREDIT:
      return [...CUSTOMER_CREDIT_FIELD_PATHS]
    default:
      return []
  }
}

export function isCustomerLookupComplete(vals: CustomerWizardFormValues): boolean {
  return Boolean(vals.allianzEhid?.trim())
}

function hasMeaningfulAllianzLimit(value: CustomerWizardFormValues['allianzCreditLimit']): boolean {
  if (value == null) return false
  const money = moneyFromFormValue(value)
  return money != null && money.amount !== 0n
}

export function validateCustomerWizardStepValues(
  stepId: CustomerWizardStepId,
  vals: CustomerWizardFormValues,
  showTraderSelect: boolean
): EntityFormErrorMap {
  const errors: EntityFormErrorMap = {}
  if (stepId === CUSTOMER_WIZARD_STEP.TRADER && showTraderSelect && (vals.traderId == null || vals.traderId < 1)) {
    errors.traderId = 'Select a trader'
  }
  if (stepId === CUSTOMER_WIZARD_STEP.ORGANIZATION && !vals.name.trim()) {
    errors.name = 'Customer name is required'
  }
  if (stepId === CUSTOMER_WIZARD_STEP.IDENTIFIERS && vals.lei && !isValidLei(vals.lei)) {
    errors.lei = LEI_VALIDATION_MESSAGE
  }
  if (
    stepId === CUSTOMER_WIZARD_STEP.CREDIT &&
    vals.creditClassification === CREDIT_CLASSIFICATION_INSURED &&
    !hasMeaningfulAllianzLimit(vals.allianzCreditLimit)
  ) {
    errors.allianzCreditLimit = 'Insured credit classification requires an Allianz credit limit'
  }
  return errors
}

export function applyCreditClassificationChange(
  draft: Draft<CustomerWizardFormValues>,
  creditClassification: CreditClassification | undefined
): void {
  if (creditClassification === CREDIT_CLASSIFICATION_IG) {
    draft.allianzRating = undefined
    draft.allianzCreditLimit = undefined
    return
  }
  if (creditClassification === CREDIT_CLASSIFICATION_INSURED) {
    draft.moodysRating = undefined
    draft.spRating = undefined
    draft.fitchRating = undefined
    draft.jcrRating = undefined
    return
  }
  draft.moodysRating = undefined
  draft.spRating = undefined
  draft.fitchRating = undefined
  draft.jcrRating = undefined
  draft.allianzRating = undefined
  draft.allianzCreditLimit = undefined
}

export function clearCustomerCreditFields(draft: Draft<CustomerWizardFormValues>): void {
  draft.creditClassification = undefined
  draft.moodysRating = undefined
  draft.spRating = undefined
  draft.fitchRating = undefined
  draft.jcrRating = undefined
  draft.allianzRating = undefined
  draft.allianzCreditLimit = undefined
}
